import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, Smartphone, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const TwoFactor = () => {
  const { API } = useAuth();
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`);
      setEnabled(response.data['2fa_enabled'] || false);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    }
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`);
      setSetup(response.data);
      toast.success('Scanează codul QR în aplicația ta de autentificare');
    } catch (error) {
      toast.error('Eroare la generarea codului 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      toast.success('2FA activat cu succes!');
      setEnabled(true);
      setSetup(null);
      setVerifyCode('');
    } catch (error) {
      toast.error('Cod invalid. Încearcă din nou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-6 md:px-12 py-8" data-testid="2fa-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
            Autentificare cu Doi Factori (2FA)
          </h1>
          <p className="text-[#4a5568] text-lg mb-8">
            Adaugă un nivel suplimentar de securitate contului tău
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Status Card */}
            <Card className="border-none card-shadow">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-[#1A4D2E] flex items-center">
                  <Shield className="w-6 h-6 mr-2" />
                  Status 2FA
                </CardTitle>
                <CardDescription>Starea actuală a autentificării cu doi factori</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-xl">
                    <div className="flex items-center space-x-3">
                      {enabled ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-6 h-6 text-orange-600" />
                      )}
                      <div>
                        <p className="font-medium text-[#1A4D2E]">
                          {enabled ? '2FA Activat' : '2FA Dezactivat'}
                        </p>
                        <p className="text-sm text-[#4a5568]">
                          {enabled 
                            ? 'Contul tău este protejat cu 2FA'
                            : 'Activează 2FA pentru securitate sporită'
                          }
                        </p>
                      </div>
                    </div>
                    <Badge className={enabled ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                      {enabled ? 'Activ' : 'Inactiv'}
                    </Badge>
                  </div>

                  {!enabled && !setup && (
                    <Button
                      onClick={handleSetup}
                      disabled={loading}
                      data-testid="setup-2fa-button"
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                    >
                      <Smartphone className="w-4 h-4 mr-2" />
                      Activează 2FA
                    </Button>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Cum funcționează 2FA?</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Instalează o aplicație de autentificare (Google Authenticator, Authy)</li>
                      <li>• Scanează codul QR generat</li>
                      <li>• Introdu codul din aplicație pentru verificare</li>
                      <li>• La fiecare autentificare vei fi solicitat un cod din aplicație</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Setup Card */}
            {setup && (
              <Card className="border-none card-shadow">
                <CardHeader>
                  <CardTitle className="font-serif text-2xl text-[#1A4D2E]">
                    Configurare 2FA
                  </CardTitle>
                  <CardDescription>Scanează codul QR și verifică</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* QR Code */}
                    <div className="bg-white p-4 rounded-xl border-2 border-[#E2E8F0]">
                      <img
                        src={setup.qr_code}
                        alt="QR Code 2FA"
                        className="w-full max-w-xs mx-auto"
                      />
                    </div>

                    {/* Secret Key */}
                    <div className="bg-[#F5F5F0] p-4 rounded-xl">
                      <p className="text-xs font-medium text-[#4a5568] mb-2">Cheie secretă (dacă nu poți scana):</p>
                      <code className="text-sm text-[#1A4D2E] break-all">{setup.secret}</code>
                    </div>

                    {/* Verification Form */}
                    <form onSubmit={handleVerify} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="verify-code">Cod de Verificare</Label>
                        <Input
                          id="verify-code"
                          data-testid="2fa-verify-code-input"
                          type="text"
                          placeholder="123456"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value)}
                          maxLength={6}
                          required
                          className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12 text-center text-2xl tracking-widest"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading || verifyCode.length !== 6}
                        data-testid="verify-2fa-button"
                        className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24]"
                      >
                        {loading ? 'Se verifică...' : 'Verifică și Activează'}
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TwoFactor;